
import { Component, Vue, Prop, Watch, Emit, Ref } from 'vue-property-decorator'
import type { LineConfig, DrawCanvasType, PointRingConfig } from '@xt/client/utils/canvas/index'
import { DrawCanvas } from '@xt/client/utils/canvas/index'

export type LineChartConfig = {
  // 线条宽度
  lineWidth: number
  // 线条颜色
  lineColor: string
  // 容器宽
  containerW: number
  // 容器高
  containerH: number
  // 坐标内圆半径
  innerRadius: number
  // 坐标外圆半径
  outerRadius: number
  // 坐标内圆颜色
  innerColor: string
  // 坐标外圆颜色
  outerColor: string
  // 填充渐变色
  fillColor: string[]
  // 数字单位
  unit: string
  // 数字文字颜色
  labelColor: string
  // 数字文字大小
  labelFontSize: number
  tension?: number
}

@Component({
  components: {}
})
export default class LineChart extends Vue {
  @Prop({ type: Array, default: () => [] }) data: number[]
  @Prop() config: LineChartConfig

  @Ref()
  canvas: HTMLCanvasElement

  drawCanvas: DrawCanvasType

  // 修改 getPoints 以便为顶部的文字留出更多空间
  getPoints(): { x: number; y: number }[] {
    const { containerW: w, containerH: h, lineWidth, labelFontSize, unit } = this.config
    // padding 需要考虑文字高度，防止文字超出画布
    const topPadding = (labelFontSize || 12) + 10
    // const sidePadding = Math.floor(lineWidth / 2) + 10
    const bottomPadding = 5
    const maxVal = Math.max(...this.data)

    // 处理超长label时候的显示截取问题
    const ctx = this.drawCanvas.canvas.getContext('2d')!
    ctx.font = `${labelFontSize}px Arial, sans-serif`

    const leftSide = Math.floor(lineWidth / 2) + Math.floor(ctx!.measureText(`${this.data[0]}${unit}`).width / 2)
    const rightSide = Math.floor(lineWidth / 2) + Math.floor(ctx!.measureText(`${this.data[this.data.length - 1]}${unit}`).width / 2)

    const validWidth = w - leftSide - rightSide
    const validHeight = h - topPadding - bottomPadding

    const range = maxVal || 1
    const stepX = validWidth / (this.data.length - 1)

    if (this.data.length === 1) {
      return [
        {
          x: leftSide,
          y: topPadding + validHeight
        },
        {
          x: w - rightSide,
          y: topPadding + validHeight
        }
      ]
    }

    return this.data.map((val, i) => {
      const x = leftSide + i * stepX
      // Y 坐标计算：起始偏移为 topPadding
      const y = topPadding + validHeight - (val / range) * validHeight

      return { x, y }
    })
  }

  drawChart() {
    this.drawCanvas = new DrawCanvas({
      canvas: this.canvas,
      config: this.config
    })
    this.drawCanvas.initCanvas()
    this.drawCanvas.clearDraw()

    const points = this.getPoints()

    if (points.length === 0) return

    const {
      lineColor,
      lineWidth,
      innerRadius,
      outerRadius,
      innerColor,
      outerColor,
      fillColor,
      unit,
      labelColor,
      labelFontSize,
      containerH,
      tension
    } = this.config

    if (fillColor && fillColor.length > 0) {
      this.drawCanvas.drawFillPath({
        points,
        fillColor,
        containerH,
        tension
      })
    }

    const lineConfig: LineConfig = {
      points,
      lineColor,
      lineWidth,
      tension
    }
    const pointRingConfig: PointRingConfig = {
      points,
      innerRadius,
      outerRadius,
      innerColor,
      outerColor
    }
    this.drawCanvas.drawLinePath(lineConfig)
    this.drawCanvas.drawPointRings(pointRingConfig)

    this.drawCanvas.drawLabels({
      points,
      values: this.data,
      unit: unit || '',
      color: labelColor || '#333',
      fontSize: labelFontSize || 12
    })
  }

  created(): void {}

  mounted(): void {
    this.drawChart()
  }

  updated(): void {}

  beforeDestroy(): void {
    this.drawCanvas.clearDraw()
  }

  destroyed(): void {}
}
